import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Button,
    InputBase,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CustomTooltip from "../../components/CustomTooltip";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { toast } from "react-hot-toast";
import FIRRequestDetailsModal from "../admin/components/FIRRequestDetailsModal";

const statusStyles = {
  UNASSIGNED: { bgcolor: "#F8D66D", color: "#fff" },
  ASSIGNED: { bgcolor: "#FFB54C", color: "#fff" },
  VISITED: { bgcolor: "#B3EBF2", color: "#fff" },
  SIGNPENDING: { bgcolor: "#A5E3C2", color: "#fff" },
  FINALREPORT: { bgcolor: "#8CD47E", color: "#fff" },
  REJECTED: { bgcolor: "#FF6961", color: "#fff" },
  // FIR Statuses
  "REQUESTED TO ADMIN": { bgcolor: "#F8D66D", color: "#fff" },
  "PROJECT MAPPED": { bgcolor: "#FFB54C", color: "#fff" },
  "ASSIGNED ENGINEER": { bgcolor: "#B3EBF2", color: "#fff" },
  "COMPLETED INSPECTION": { bgcolor: "#8CD47E", color: "#fff" },
  "REPORT SUBMITTED": { bgcolor: "#8CD47E", color: "#fff" },
  "COMPLETED": { bgcolor: "#8CD47E", color: "#fff" },
};

const ProjectList = ({ status, title, source }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { token, userData, hasRole } = useAuth() || {};
    const isIndividualClient = hasRole?.("CLIENT") && userData?.clientRole?.roleType === "Individual";

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

    // Filter states - Updated to match AllProjects
    const [searchQuery, setSearchQuery] = useState("");
    const [searchDebounce, setSearchDebounce] = useState("");
    const [selectedDate, setSelectedDate] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        column: null, // 'projectName', 'engineer', 'date'
        direction: null, // 'asc', 'desc'
    });
    const [dateFilter, setDateFilter] = useState("ALL");
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [isCreatingProject, setIsCreatingProject] = useState(null);
    const [tempDate, setTempDate] = useState(null);
    const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
    const [sortAnchorEl, setSortAnchorEl] = useState(null);

    // New filter states matching AllProjects
    const [engineers, setEngineers] = useState([]);
    const [selectedEngineers, setSelectedEngineers] = useState([]);
    const [allProjectNames, setAllProjectNames] = useState([]);
    const [selectedProjectNames, setSelectedProjectNames] = useState([]);
    const [engineerMap, setEngineerMap] = useState({});
    const [showFilters, setShowFilters] = useState(false);
    const [filterMenusOpen, setFilterMenusOpen] = useState({
        engineers: false,
        projects: false,
    });
    const [engineersLoaded, setEngineersLoaded] = useState(false);
    
    // Assign Engineer state
    const [assignDialog, setAssignDialog] = useState({ open: false, project: null, assignedEngineers: [] });
    const [assigningLoading, setAssigningLoading] = useState(false);
    
    // Download state
    const [downloadProgress, setDownloadProgress] = useState({});

    // FIR Request Review state
    const [firModalState, setFirModalState] = useState({ open: false, request: null, editMode: false });
    const [alertModal, setAlertModal] = useState({ open: false, message: "" });

    // Sorting state - simplified
    const [selectedSort, setSelectedSort] = useState("latest");

    // Fetch all engineers for mapping IDs to names
    const fetchEngineers = useCallback(async () => {
        try {
            const response = await axios.get(`${url}/api/users/fetch`, {
                params: { role: "ENGINEER", status: "APPROVED" },
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.users) {
                setEngineers(response.data.users);
                const map = {};
                response.data.users.forEach(user => {
                    map[user._id] = user.name;
                });
                setEngineerMap(map);
                setEngineersLoaded(true);
            }
        } catch (err) {
            console.error("Error fetching engineers:", err);
            setEngineersLoaded(true); // Continue even on error
        }
    }, [token]);

    const handleAutoCreateProject = async (firRequest) => {
        setIsCreatingProject(firRequest._id);
        try {
            const projectName = firRequest.propertyName || firRequest.projectName || firRequest.buildingName || `FIR Request (${firRequest.propertyType || 'Unknown'})`;
            
            // Clean up address parts
            const parts = [firRequest.fullAddress, firRequest.city, firRequest.state].filter(Boolean);
            let addressStr = parts.join(", ");
            if (firRequest.pincode) {
                addressStr += ` - ${firRequest.pincode}`;
            }

            const clientName = firRequest.fullName || firRequest.clientName || "";
            const contactNumber = firRequest.mobileNumber || firRequest.contactNumber || "";
            const companyName = firRequest.builderName || "";
            const propertyType = firRequest.propertyType || "";

            const formData = {
                projectName,
                address: addressStr,
                projectStatus: "inprogress",
                clientName,
                companyName,
                contactNumber,
                assignedEngineers: [],
                source: "FIR",
                firStatus: "Project Mapped",
                propertyType,
                firRequestId: firRequest._id,
            };

            const response = await axios.post(`${url}/api/projects/create`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (response.data.success) {
                toast.success("Project automatically created!");
                
                // Immediately open Assign Dialog
                setAssignDialog({ 
                    open: true, 
                    project: response.data.data, 
                    assignedEngineers: [] 
                });
                
                // Refresh the list to reflect "Project Mapped" status
                fetchProjects();
            }
        } catch (error) {
            console.error("Error auto-creating project:", error);
            toast.error(error.response?.data?.message || "Failed to auto-create project");
        } finally {
            setIsCreatingProject(null);
        }
    };

    // Fetch all project names for the filter
    const fetchAllProjectNames = useCallback(async () => {
        try {
            const response = await axios.get(`${url}/api/projects/all`, {
                params: { limit: 1000 }, // Fetch a large number to get all names
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                const names = [...new Set(response.data.data.map(p => p.projectName))].filter(Boolean);
                setAllProjectNames(names.sort());
            }
        } catch (err) {
            console.error("Error fetching project names:", err);
        }
    }, [token]);

    const fetchProjects = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                status: status,
                limit: 100,
                ...(source && { source }),
            };

            // If user is an engineer, filter projects assigned to them
            if (hasRole("ENGINEER") && userData?._id) {
                params.engineer = userData._id;
            }

            // Add search query if exists
            if (searchDebounce.trim()) {
                params.search = searchDebounce.trim();
            }

            // Add project name filter
            if (selectedProjectNames.length > 0) {
                params.projectName = selectedProjectNames.join(",");
            }

            // Add engineer filter
            if (selectedEngineers.length > 0) {
                // Convert engineer names to IDs
                const engineerIds = selectedEngineers.map(name => {
                    const engineer = engineers.find(eng => eng.name === name);
                    return engineer?._id;
                }).filter(id => id);
                if (engineerIds.length > 0) {
                    params.engineer = engineerIds.join(",");
                }
            }

            let endpoint = `${url}/api/projects/all`;
            if (source === "FIR") {
                endpoint = `${url}/api/fir/requests`;
            }

            const response = await axios.get(endpoint, {
                params,
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data.success) {
                setProjects(response.data.data);
            }
        } catch (err) {
            console.error("Error fetching projects:", err);
            setError("Failed to load projects");
        } finally {
            setLoading(false);
        }
    }, [status, token, hasRole, userData, searchDebounce, selectedEngineers, selectedProjectNames, engineers]);

    useEffect(() => {
        if (token) {
            fetchEngineers();
            fetchAllProjectNames();
        }
    }, [fetchEngineers, fetchAllProjectNames, token]);

    useEffect(() => {
        if (token) {
            fetchProjects();
        }
    }, [fetchProjects, token, location.key, location.pathname]);

    // Auto-refetch when returning to the tab/window
    useEffect(() => {
        const handleFocus = () => {
            if (token) fetchProjects();
        };
        window.addEventListener("focus", handleFocus);
        return () => window.removeEventListener("focus", handleFocus);
    }, [token, fetchProjects]);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const handleAssignSubmit = async () => {
        if (!assignDialog.project || assignDialog.assignedEngineers.length === 0) {
            toast.error("Please select at least one engineer");
            return;
        }

        try {
            setAssigningLoading(true);
            const response = await axios.put(`${url}/api/projects/update/${assignDialog.project._id}`, {
                assignedEngineers: assignDialog.assignedEngineers
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                toast.success("Engineers assigned successfully");
                const targetId = assignDialog.project._id;
                setAssignDialog({ open: false, project: null, assignedEngineers: [] });
                navigate(`/technical/start-mapping/${targetId}`);
            }
        } catch (err) {
            console.error("Error assigning engineers:", err);
            toast.error(err.response?.data?.message || "Failed to assign engineers");
        } finally {
            setAssigningLoading(false);
        }
    };

    const triggerDelete = (projectId, isFirRequest = false, isConvertedFir = false) => {
        setDeleteConfirm({ open: true, id: projectId, isFirRequest, isConvertedFir });
    };

    const executeDelete = async () => {
        if (!deleteConfirm.id) return;
        try {
            if (hasRole("ADMIN") || hasRole("SUPERADMIN")) {
                let deleteUrl = `${url}/api/projects/delete/${deleteConfirm.id}`;
                if (deleteConfirm.isFirRequest && !deleteConfirm.isConvertedFir) {
                    deleteUrl = `${url}/api/fir/requests/delete/${deleteConfirm.id}`;
                }
                
                await axios.delete(deleteUrl, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Project deleted successfully");
                fetchProjects();
            }
        } catch (err) {
            console.error("Delete error:", err);
            toast.error(err.response?.data?.message || "Failed to delete project");
        } finally {
            setDeleteConfirm({ open: false, id: null, isFirRequest: false, isConvertedFir: false });
        }
    };

    const handleDownloadReport = async (projectId, projectItem = null) => {
        try {
            if (!token) {
                toast.error("Please login to download reports");
                return;
            }

            setDownloadProgress(prev => ({ ...prev, [projectId]: 0 }));

            const userRole = userData?.role || "ENGINEER";
            
            const onDownloadProgress = (progressEvent) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setDownloadProgress(prev => ({ ...prev, [projectId]: percentCompleted }));
                } else {
                    setDownloadProgress(prev => ({ ...prev, [projectId]: 50 }));
                }
            };

            if (projectItem?.source === "FIR" || source === "FIR" || projectItem?.firStatus) {
                try {
                    const response = await axios.post(
                        `${url}/api/field-inspection-reports/download-aggregated`,
                        { projectId: projectId },
                        {
                            headers: { Authorization: `Bearer ${token}` },
                            responseType: 'blob',
                            timeout: 900000,
                            onDownloadProgress
                        }
                    );

                    const contentType = response.headers["content-type"] || "";
                    if (!contentType.includes("application/json")) {
                        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
                        const downloadUrl = window.URL.createObjectURL(pdfBlob);
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = `FIR_${projectItem?.projectName || 'Report'}_${new Date().toISOString().slice(0, 10)}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(downloadUrl);
                        toast.success("Report downloaded successfully");
                        setDownloadProgress(prev => { const next = { ...prev }; delete next[projectId]; return next; });
                        return;
                    }
                } catch (firErr) {
                    console.warn("Aggregated FIR report download fallback:", firErr);
                }
            }

            const response = await axios.get(
                `${url}/api/admin/download-checklist-report/${projectId}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { userRole: userRole },
                    responseType: 'blob',
                    timeout: 900000,
                    onDownloadProgress
                }
            );

            const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                let filename = null;
                const match = contentDisposition.match(/filename="([^"]+)"/i);
                if (match && match[1]) {
                    filename = match[1];
                } else {
                    const match2 = contentDisposition.match(/filename=([^;]+)/i);
                    if (match2 && match2[1]) filename = match2[1].trim();
                }

                if (filename) {
                    link.download = filename;
                } else {
                    link.download = `checklist-report-${projectId}.pdf`;
                }
            } else {
                link.download = `checklist-report-${projectId}.pdf`;
            }

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);

            toast.success("Report downloaded successfully");

        } catch (err) {
            console.error("Error downloading report:", err);
            toast.error(err.response?.data?.message || "Failed to download report");
        } finally {
            setDownloadProgress(prev => { const next = { ...prev }; delete next[projectId]; return next; });
        }
    };


    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const getEngineerName = (eng) => {
        if (typeof eng === 'string') {
            return engineerMap[eng] || eng;
        }
        return eng.name || eng;
    };

    // Filtered and Sorted Projects
    const processedProjects = useMemo(() => {
        let filtered = [...projects];

        // Filter by Date
        if (dateFilter === "TODAY") {
            const today = new Date();
            const todayStr = today.toDateString();
            filtered = filtered.filter(p => {
                const pDateStr = new Date(p.createdAt || p.requestedDate || p.createdDate || p.updatedAt).toDateString();
                return pDateStr === todayStr;
            });
        } else if (dateFilter === "YESTERDAY") {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayStr = yesterday.toDateString();
            filtered = filtered.filter(p => {
                const pDateStr = new Date(p.createdAt || p.requestedDate || p.createdDate || p.updatedAt).toDateString();
                return pDateStr === yesterdayStr;
            });
        } else if (dateFilter === "TOMORROW") {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const tomorrowStr = tomorrow.toDateString();
            filtered = filtered.filter(p => {
                const pDateStr = new Date(p.createdAt || p.requestedDate || p.createdDate || p.updatedAt).toDateString();
                return pDateStr === tomorrowStr;
            });
        } else if (selectedDate) {
            const filterDateStr = selectedDate.toDateString();
            filtered = filtered.filter(p => {
                const pDateStr = new Date(p.createdAt || p.requestedDate || p.createdDate || p.updatedAt).toDateString();
                return pDateStr === filterDateStr;
            });
        }

        // Filter by Project Name
        if (selectedProjectNames.length > 0) {
            filtered = filtered.filter(p => selectedProjectNames.includes(p.projectName));
        }

        // Filter by Engineer
        if (selectedEngineers.length > 0) {
            filtered = filtered.filter(p => {
                const projectEngineers = (p.assignedEngineers || []).map(eng => getEngineerName(eng));
                return selectedEngineers.some(name => projectEngineers.includes(name));
            });
        }

        if (searchDebounce.trim()) {
            const searchTerm = searchDebounce.toLowerCase().trim();
            filtered = filtered.filter(p => 
                p.projectName?.toLowerCase().includes(searchTerm) ||
                p.companyName?.toLowerCase().includes(searchTerm) ||
                p.propertyName?.toLowerCase().includes(searchTerm) ||
                p.buildingName?.toLowerCase().includes(searchTerm) ||
                p.clientName?.toLowerCase().includes(searchTerm) ||
                p.fullName?.toLowerCase().includes(searchTerm) ||
                p.propertyType?.toLowerCase().includes(searchTerm) ||
                p.assignedEngineers?.some(eng => 
                    getEngineerName(eng).toLowerCase().includes(searchTerm)
                )
            );
        }

        // Sort projects
        if (sortConfig.column) {
            filtered.sort((a, b) => {
                let aValue, bValue;

                switch (sortConfig.column) {
                    case 'projectName':
                        aValue = a.projectName?.toLowerCase() || '';
                        bValue = b.projectName?.toLowerCase() || '';
                        break;
                    case 'engineer':
                        aValue = getEngineerName(a.assignedEngineers?.[0])?.toLowerCase() || '';
                        bValue = getEngineerName(b.assignedEngineers?.[0])?.toLowerCase() || '';
                        break;
                    case 'date':
                        aValue = new Date(a.latestReportDate || a.updatedAt || a.createdDate);
                        bValue = new Date(b.latestReportDate || b.updatedAt || b.createdDate);
                        break;
                    default:
                        return 0;
                }

                if (sortConfig.direction === 'asc') {
                    return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
                } else {
                    return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
                }
            });
        } else {
            // Default sort by selectedSort
            if (selectedSort === "latest") {
                filtered.sort((a, b) => new Date(b.latestReportDate || b.updatedAt || b.createdDate) - new Date(a.latestReportDate || a.updatedAt || a.createdDate));
            } else if (selectedSort === "oldest") {
                filtered.sort((a, b) => new Date(a.latestReportDate || a.updatedAt || a.createdDate) - new Date(b.latestReportDate || b.updatedAt || b.createdDate));
            } else if (selectedSort === "AZ") {
                filtered.sort((a, b) => a.projectName?.localeCompare(b.projectName));
            } else if (selectedSort === "ZA") {
                filtered.sort((a, b) => b.projectName?.localeCompare(a.projectName));
            }
        }

        return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, searchDebounce, selectedDate, dateFilter, sortConfig, selectedSort, engineerMap, selectedProjectNames, selectedEngineers]);

    // Check if any filter is applied
    const isFilterApplied = () => {
        return searchQuery.trim() !== "" ||
            selectedEngineers.length > 0 ||
            selectedProjectNames.length > 0 ||
            dateFilter !== "ALL" ||
            selectedSort !== "latest" ||
            sortConfig.column !== null;
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchQuery("");
        setSelectedEngineers([]);
        setSelectedProjectNames([]);
        setDateFilter("ALL");
        setSelectedDate(null);
        setSelectedSort("latest");
        setSortConfig({ column: null, direction: null });
    };

    // Handle column sort click
    const handleColumnSort = (column) => {
        setSortConfig(prev => {
            if (prev.column === column) {
                // Toggle direction if same column
                return {
                    column: column,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            } else {
                // New column, start with ascending
                return {
                    column: column,
                    direction: 'asc'
                };
            }
        });
        // Clear global sort when column sort is used
        setSelectedSort("latest");
    };

    // Get sort icon and color for a column
    const getSortIconForColumn = (column) => {
        if (sortConfig.column === column) {
            return sortConfig.direction === 'asc' ? 'foldUp' : 'foldDown';
        }
        return 'sort';
    };

    const getSortIconColorForColumn = (column) => {
        if (sortConfig.column === column) {
            return COLORS.PRIMARY;
        }
        return COLORS.LIGHT_SECONDARY;
    };

    // Handle calendar filter selection
    const handleCalendarFilterSelect = (filterValue) => {
        if (filterValue === "CUSTOM") {
            setTempDate(selectedDate);
            setShowDatePicker(true);
        } else {
            setDateFilter(filterValue);
            setSelectedDate(null);
        }
        setCalendarAnchorEl(null);
    };



    const commonMenuProps = {
        PaperProps: {
            sx: {
                maxHeight: 250,
                width: 280,
                overflow: 'auto',
                mt: 0.5,
                '& .MuiMenuItem-root': {
                    minHeight: 40,
                    padding: '6px 12px',
                },
                '& .MuiCheckbox-root': {
                    padding: '6px',
                }
            }
        },
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography
                        sx={{ ...TYPOGRAPHY.H3, fontFamily: FONT_FAMILY, color: COLORS.SECONDARY }}
                    >
                        {title}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
                        {/* Search Bar */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexGrow: 0,
                                width: { xs: 150, sm: 200, md: 220 },
                                px: 1.5,
                                py: 0.4,
                                borderRadius: 1.5,
                                border: `1px solid ${COLORS.PRIMARY}`,
                                backgroundColor: COLORS.BACKGROUND,
                                color: COLORS.PRIMARY,
                            }}
                        >
                            <Icon name="search" sx={{ fontSize: 16, color: "currentColor" }} />
                            <InputBase
                                placeholder="Search"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{
                                    flex: 1,
                                    fontFamily: FONT_FAMILY,
                                    fontSize: '14px',
                                    color: COLORS.TEXT,
                                    ml: 1,
                                    mt: 0,
                                    '& input::placeholder': {
                                        fontSize: '14px',
                                    },
                                }}
                            />
                            {searchQuery && (
                                <IconButton
                                    size="small"
                                    onClick={() => setSearchQuery("")}
                                    sx={{ padding: 0.5 }}
                                >
                                    <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
                                </IconButton>
                            )}
                        </Box>

                        {/* Calendar Menu */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CustomTooltip title="Calendar Filter">
                                <IconButton
                                    onClick={(e) => setCalendarAnchorEl(e.currentTarget)}
                                    sx={{
                                        color: dateFilter !== "ALL" || selectedDate ? COLORS.PRIMARY : COLORS.SECONDARY,
                                    }}
                                >
                                    <Icon name="calendar2" />
                                </IconButton>
                            </CustomTooltip>
                        </Box>

                        {/* Sort Menu */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CustomTooltip title="Sort Options">
                                <IconButton
                                    onClick={(e) => setSortAnchorEl(e.currentTarget)}
                                    sx={{
                                        color: selectedSort !== "latest" || sortConfig.column ? COLORS.PRIMARY : COLORS.SECONDARY,
                                    }}
                                >
                                    <Icon name="sort" />
                                </IconButton>
                            </CustomTooltip>
                        </Box>

                        {/* Filter Button */}
                        {!isIndividualClient && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CustomTooltip title="Show Filters">
                                    <IconButton
                                        onClick={() => setShowFilters(!showFilters)}
                                        sx={{
                                            color: showFilters ? COLORS.PRIMARY : COLORS.SECONDARY,
                                        }}
                                    >
                                        <Icon name="list" />
                                    </IconButton>
                                </CustomTooltip>
                            </Box>
                        )}

                        {isFilterApplied() && (
                            <CustomTooltip title="Clear Filters">
                                <IconButton onClick={clearFilters}>
                                    <Icon name="rotate" color={COLORS.ERROR} />
                                </IconButton>
                            </CustomTooltip>
                        )}
                    </Box>
                </Box>

                {/* Independent Alert */}
                {source === "FIR" && (hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
                    <Alert severity="warning" sx={{ mb: 3, fontFamily: FONT_FAMILY, alignItems: 'center', width: '100%' }}>
                        <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, fontWeight: 700, fontSize: '1.15rem' }}>
                            Note: Please assign an Engineer first!
                        </Typography>
                        <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, fontSize: '0.95rem', mt: 0.5 }}>
                            This will automatically create a proper project for the request so you can proceed further.
                        </Typography>
                    </Alert>
                )}

                {/* Filter Section - Horizontal Boxes */}
                {showFilters && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 3,
                        alignItems: 'center',
                        flexWrap: 'wrap'
                    }}>
                        {/* Project Name Filter */}
                        {title !== "Client Requests" && (
                        <Box sx={{ flex: 1, }}>
                            <Typography
                                sx={{
                                    ...TYPOGRAPHY.H4,
                                    color: COLORS.PRIMARY,
                                    mb: 0.5,

                                }}
                            >
                                Project Name
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    multiple
                                    value={selectedProjectNames}
                                    onChange={(e) => {
                                        setSelectedProjectNames(e.target.value);
                                    }}
                                    open={filterMenusOpen.projects}
                                    onOpen={() => setFilterMenusOpen(prev => ({ ...prev, projects: true }))}
                                    onClose={() => setFilterMenusOpen(prev => ({ ...prev, projects: false }))}
                                    input={<OutlinedInput />}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) {
                                            return (
                                                <Typography sx={{
                                                    color: COLORS.LIGHT_SECONDARY,
                                                    fontSize: '0.875rem'
                                                }}>
                                                    Select Projects
                                                </Typography>
                                            );
                                        }
                                        return selected.length <= 2
                                            ? selected.join(', ')
                                            : `${selected.length} selected`;
                                    }}
                                    displayEmpty
                                    sx={{
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: COLORS.PRIMARY,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: COLORS.PRIMARY,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: COLORS.PRIMARY,
                                        },
                                    }}
                                    MenuProps={commonMenuProps}
                                >
                                    {allProjectNames.length > 0 ? (
                                        allProjectNames.map((name) => (
                                            <MenuItem
                                                key={name}
                                                value={name}
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word',
                                                }}
                                            >
                                                <Checkbox
                                                    checked={selectedProjectNames.indexOf(name) > -1}
                                                    size="small"
                                                />
                                                <ListItemText
                                                    primary={name}
                                                    sx={{
                                                        margin: 0,
                                                        '& .MuiTypography-root': {
                                                            fontSize: '0.85rem',
                                                            fontWeight: 400,
                                                        }
                                                    }}
                                                />
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>
                                            <Typography sx={{
                                                fontSize: '0.85rem',
                                                color: COLORS.TEXT,
                                                fontStyle: 'italic'
                                            }}>
                                                No projects available
                                            </Typography>
                                        </MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                        )}

                        {/* Engineer Filter (only for ADMIN/SUPERADMIN) */}
                        {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
                            <Box sx={{ flex: 1, }}>
                                <Typography
                                    sx={{
                                        ...TYPOGRAPHY.BODY_SMALL,
                                        color: COLORS.PRIMARY,
                                        mb: 0.5,
                                        fontWeight: 600
                                    }}
                                >
                                    Engineer
                                </Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={selectedEngineers}
                                        onChange={(e) => {
                                            setSelectedEngineers(e.target.value);
                                        }}
                                        open={filterMenusOpen.engineers}
                                        onOpen={() => setFilterMenusOpen(prev => ({ ...prev, engineers: true }))}
                                        onClose={() => setFilterMenusOpen(prev => ({ ...prev, engineers: false }))}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => {
                                            if (selected.length === 0) {
                                                return (
                                                    <Typography sx={{
                                                        color: COLORS.LIGHT_SECONDARY,
                                                        fontSize: '0.875rem'
                                                    }}>
                                                        Select Engineers
                                                    </Typography>
                                                );
                                            }
                                            return selected.length <= 2
                                                ? selected.join(', ')
                                                : `${selected.length} selected`;
                                        }}
                                        displayEmpty
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: COLORS.PRIMARY,
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: COLORS.PRIMARY,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: COLORS.PRIMARY,
                                            },
                                        }}
                                        MenuProps={commonMenuProps}
                                    >
                                        {engineers.length > 0 ? (
                                            engineers.map((engineer) => (
                                                <MenuItem
                                                    key={engineer._id}
                                                    value={engineer.name}
                                                    sx={{
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={selectedEngineers.indexOf(engineer.name) > -1}
                                                        size="small"
                                                    />
                                                    <ListItemText
                                                        primary={engineer.name}
                                                        sx={{
                                                            margin: 0,
                                                            '& .MuiTypography-root': {
                                                                fontSize: '0.85rem',
                                                                fontWeight: 400,
                                                            }
                                                        }}
                                                    />
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled>
                                                <Typography sx={{
                                                    fontSize: '0.85rem',
                                                    color: COLORS.TEXT,
                                                    fontStyle: 'italic'
                                                }}>
                                                    No engineers available
                                                </Typography>
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}

                        {/* Date Filter Display Box */}
                        {/* <Box sx={{ flex: 0.5, minWidth: 150, maxWidth: 200 }}>
                            <Typography
                                sx={{
                                    ...TYPOGRAPHY.BODY_SMALL,
                                    color: COLORS.PRIMARY,
                                    mb: 0.5,
                                    fontWeight: 600
                                }}
                            >
                                Date Filter
                            </Typography>
                            <Box
                                sx={{
                                    border: `1px solid ${COLORS.PRIMARY}`,
                                    borderRadius: 1,
                                    p: 1,
                                    backgroundColor: COLORS.BACKGROUND,
                                    minHeight: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography sx={{ fontSize: '0.875rem', color: COLORS.TEXT }}>
                                    {getDateFilterDisplay()}
                                </Typography>
                            </Box>
                        </Box> */}
                    </Box>
                )}

                {/* Error Message */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Table Container */}
                <Box sx={{
                    width: '100%',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    '&::-webkit-scrollbar': {
                        height: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: COLORS.LIGHT_SECONDARY,
                        borderRadius: '4px',
                    },
                }}>
                    <Paper elevation={0} sx={{ backgroundColor: "transparent", minWidth: '800px' }}>
                        <TableContainer>
                            <Table
                                sx={{
                                    backgroundColor: "transparent",
                                    "& thead th": {
                                        backgroundColor: "transparent",
                                        padding: "8px 6px",
                                    },
                                    "& tbody td": {
                                        backgroundColor: "transparent",
                                        borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                                        padding: "8px 6px",
                                        verticalAlign: 'top',
                                    },
                                    tableLayout: 'fixed',
                                    width: '100%',
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                ...TYPOGRAPHY.H4,
                                                color: COLORS.PRIMARY,
                                                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                                                whiteSpace: "nowrap",
                                                fontSize: "0.85rem",
                                                fontWeight: 600,
                                                width: '200px',
                                                minWidth: '200px',
                                                maxWidth: '200px',
                                                padding: "8px 6px",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <span style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {source === "FIR" ? "Property Name" : "Project Name"}
                                                </span>
                                                <CustomTooltip title="Sort by Project Name">
                                                    <Icon
                                                        name={getSortIconForColumn('projectName')}
                                                        color={getSortIconColorForColumn('projectName')}
                                                        sx={{
                                                            fontSize: 14,
                                                            cursor: "pointer",
                                                            flexShrink: 0,
                                                            "&:hover": {
                                                                color: COLORS.PRIMARY,
                                                            },
                                                        }}
                                                        onClick={() => handleColumnSort('projectName')}
                                                    />
                                                </CustomTooltip>
                                            </Box>
                                        </TableCell>
                                        {source === "FIR" ? (
                                            <>
                                                <TableCell sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, whiteSpace: "nowrap", width: '130px', minWidth: '130px', maxWidth: '130px', padding: "8px 6px" }}>
                                                    Client Name
                                                </TableCell>
                                                <TableCell sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, whiteSpace: "nowrap", width: '100px', minWidth: '100px', maxWidth: '100px', padding: "8px 6px" }}>
                                                    Property Type
                                                </TableCell>
                                                <TableCell sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`, whiteSpace: "nowrap", width: '80px', minWidth: '80px', maxWidth: '80px', padding: "8px 6px" }}>
                                                    Req Date
                                                </TableCell>
                                            </>
                                        ) : (
                                            <TableCell
                                                sx={{
                                                    ...TYPOGRAPHY.H4,
                                                    color: COLORS.PRIMARY,
                                                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                                                    whiteSpace: "nowrap",
                                                    width: '140px',
                                                    minWidth: '140px',
                                                    maxWidth: '140px',
                                                    padding: "8px 6px",
                                                }}
                                            >
                                                Latest Report
                                            </TableCell>
                                        )}
                                        {!hasRole("ENGINEER") && (
                                            <TableCell
                                                sx={{
                                                    ...TYPOGRAPHY.H4,
                                                    color: COLORS.PRIMARY,
                                                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                                                    whiteSpace: "nowrap",

                                                    width: '120px',
                                                    minWidth: '120px',
                                                    maxWidth: '120px',
                                                    padding: "10px 8px",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <span style={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {source === "FIR" ? "Engineer / Action" : "Engineer"}
                                                    </span>

                                                </Box>
                                            </TableCell>
                                        )}
                                        <TableCell
                                            sx={{
                                                ...TYPOGRAPHY.H4,
                                                color: COLORS.PRIMARY,
                                                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                                                whiteSpace: "nowrap",

                                                width: '130px',
                                                minWidth: '130px',
                                                maxWidth: '130px',
                                                padding: "10px 8px",
                                            }}
                                        >
                                            Status
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                ...TYPOGRAPHY.H4,
                                                color: COLORS.PRIMARY,
                                                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                                                whiteSpace: "nowrap",
                                                textAlign: "center",
                                                width: '100px',
                                                minWidth: '100px',
                                                maxWidth: '100px',
                                                padding: "10px 8px",
                                            }}
                                        >
                                            Mapping
                                        </TableCell>
                                        <TableCell
                                            align="center"
                                            sx={{
                                                ...TYPOGRAPHY.H4,
                                                color: COLORS.PRIMARY,
                                                borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                                                whiteSpace: "nowrap",
                                                
                                                width: '150px',
                                                minWidth: '150px',
                                                maxWidth: '150px',
                                                padding: "8px 6px",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 0.5,
                                                }}
                                            >
                                                <span style={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    Actions
                                                </span>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {!engineersLoaded || (loading && projects.length === 0) ? (
                                        null
                                    ) : processedProjects.length === 0 ? (
                                        null
                                    ) : (
                                        processedProjects.map((project) => {
                                            const isFir = project.source === "FIR" || source === "FIR" || project.firRequestId || project.originalFirId;
                                            const hasReport = !!project.hasReport || (project.reportCount && project.reportCount > 0) || (project.fieldInspectionSummary && project.fieldInspectionSummary.totalReports > 0);
                                            const isCompleted = hasReport || project.firStatus === "Completed Inspection" || project.firStatus === "Report Submitted" || project.projectStatus === "completed" || project.status === "FINALREPORT";
                                            
                                            let currentStatus = isFir
                                                ? (project.firStatus || (project.components?.length > 0 ? "Project Mapped" : (project.assignedEngineers?.length > 0 ? "Assigned Engineer" : "Requested to Admin")))
                                                : (project.projectStatus === "completed" ? "Completed" : "In Progress");

                                            if (isCompleted) {
                                                currentStatus = "Report Submitted";
                                            }

                                            return (
                                            <TableRow key={project._id} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                                                {/* Project Name Column */}
                                                <TableCell sx={{
                                                    width: '200px',
                                                    minWidth: '200px',
                                                    maxWidth: '200px',
                                                    padding: "8px 6px"
                                                }}>
                                                    <Typography
                                                        sx={{
                                                            ...TYPOGRAPHY.BODY_LARGE,
                                                            color: COLORS.TEXT,
                                                            fontFamily: FONT_FAMILY,
                                                            wordBreak: 'break-word',
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        {source === "FIR" 
                                                          ? (project.propertyName || project.firDetails?.propertyName || project.projectName || project.firDetails?.projectName || project.buildingName || project.firDetails?.buildingName || `FIR Request (${project.firDetails?.propertyType || project.propertyType || 'Unknown'})`)
                                                          : (project.projectName || project.propertyName || project.buildingName || `FIR Request (${project.firDetails?.propertyType || project.propertyType || 'Unknown'})`)
                                                        }
                                                    </Typography>
                                                    {(project.address || project.clientAddress) && (
                                                        <Typography
                                                            sx={{
                                                                ...TYPOGRAPHY.BODY_SMALL,
                                                                color: COLORS.TEXT,
                                                                fontSize: "0.7rem",
                                                                wordBreak: 'break-word',
                                                                lineHeight: 1.2,
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            {project.address || project.clientAddress}
                                                        </Typography>
                                                    )}
                                                    {source !== "FIR" && (
                                                        <>
                                                            {(project.companyName || project.builderName) && (project.companyName || project.builderName) !== project.clientName && (
                                                                <Typography
                                                                    sx={{
                                                                        ...TYPOGRAPHY.BODY_STANDARD,
                                                                        color: COLORS.TEXT,
                                                                        wordBreak: 'break-word',
                                                                    }}
                                                                >
                                                                    Developer: {project.companyName || project.builderName}
                                                                </Typography>
                                                            )}
                                                            <Typography
                                                                sx={{
                                                                    ...TYPOGRAPHY.BODY_STANDARD,
                                                                    color: COLORS.MUTED_TEXT,
                                                                    wordBreak: 'break-word',
                                                                    mt: 0.25,
                                                                }}
                                                            >
                                                                Client: {project.clientName || project.fullName || "-"} {(project.contactNumber || project.mobileNumber) ? ` | ${project.contactNumber || project.mobileNumber}` : ""}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </TableCell>

                                                {source === "FIR" ? (
                                                    <>
                                                        <TableCell sx={{ width: '130px', minWidth: '130px', maxWidth: '130px', padding: "8px 6px" }}>
                                                              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, wordBreak: 'break-word' }}>
                                                                  {project.clientName || project.fullName || "-"}
                                                              </Typography>
                                                            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT, fontSize: "0.7rem", wordBreak: 'break-word' }}>
                                                                {project.contactNumber || project.mobileNumber || "-"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ width: '100px', minWidth: '100px', maxWidth: '100px', padding: "8px 6px" }}>
                                                            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, wordBreak: 'break-word' }}>
                                                                {project.firDetails?.propertyType || project.propertyType || "-"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ width: '80px', minWidth: '80px', maxWidth: '80px', padding: "8px 6px" }}>
                                                            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, wordBreak: 'break-word' }}>
                                                                {formatDate(project.createdAt || project.requestedDate)}
                                                            </Typography>
                                                        </TableCell>
                                                    </>
                                                ) : (
                                                    <TableCell sx={{
                                                        width: '140px',
                                                        minWidth: '140px',
                                                        maxWidth: '140px',
                                                        padding: "8px 6px"
                                                    }}>
                                                        <Typography
                                                            sx={{
                                                                ...TYPOGRAPHY.BODY_LARGE,
                                                                color: COLORS.TEXT,
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            {formatDate(project.updatedAt || project.createdDate)}
                                                        </Typography>
                                                    </TableCell>
                                                )}

                                                {/* Engineer Column */}
                                                {!hasRole("ENGINEER") && (
                                                    <TableCell sx={{
                                                        width: '120px',
                                                        minWidth: '120px',
                                                        maxWidth: '120px',
                                                        padding: "8px 6px"
                                                    }}>
                                                        <Box>
                                                            {project.assignedEngineers && project.assignedEngineers.length > 0 ? (
                                                                project.assignedEngineers.map((eng, index) => (
                                                                    <Typography
                                                                        key={index}
                                                                        sx={{
                                                                            ...TYPOGRAPHY.BODY_LARGE,
                                                                            color: COLORS.TEXT,
                                                                            mb: 0.5
                                                                        }}
                                                                    >
                                                                        {getEngineerName(eng)}
                                                                    </Typography>
                                                                ))
                                                            ) : (
                                                                (!hasRole("ADMIN") && !hasRole("SUPERADMIN")) && <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.MUTED_TEXT }}>-</Typography>
                                                            )}

                                                            {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && !(project.assignedEngineers?.length > 0) && (
                                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                                    <Typography sx={{ fontSize: "11px", color: COLORS.REJECTED || "#f44336", fontWeight: 600 }}>
                                                                        *Please assign Engineer
                                                                    </Typography>
                                                                    {(project.source === "FIR" || source === "FIR") && (!project.firStatus || project.firStatus === "Requested to Admin" || project.firStatus === "Pending") ? (
                                                                    <Button 
                                                                        size="small" 
                                                                        variant="contained" 
                                                                        sx={{ 
                                                                            fontSize: "11px", 
                                                                            py: 0.5, 
                                                                            px: 1, 
                                                                            mt: 0,
                                                                            backgroundColor: COLORS.SECONDARY, 
                                                                            color: "#fff",
                                                                            textTransform: "none",
                                                                            fontWeight: 600,
                                                                            borderRadius: "6px",
                                                                            alignSelf: "flex-start",
                                                                            width: "fit-content",
                                                                            "&:hover": {
                                                                                backgroundColor: COLORS.PRIMARY
                                                                            }
                                                                        }}
                                                                        onClick={() => handleAutoCreateProject(project)}
                                                                        disabled={isCreatingProject === project._id}
                                                                    >
                                                                        {isCreatingProject === project._id ? <CircularProgress size={16} color="inherit" /> : "Assign Engineer"}
                                                                    </Button>
                                                                ) : (
                                                                    <Button 
                                                                        size="small" 
                                                                        variant="contained" 
                                                                        sx={{ 
                                                                            fontSize: "11px", 
                                                                            py: 0.5, 
                                                                            px: 1.5, 
                                                                            mt: 0.5,
                                                                            backgroundColor: COLORS.SECONDARY, 
                                                                            color: "#fff",
                                                                            textTransform: "none",
                                                                            fontWeight: 600,
                                                                            borderRadius: "6px",
                                                                            alignSelf: "flex-start",
                                                                            width: "fit-content",
                                                                            "&:hover": {
                                                                                backgroundColor: COLORS.PRIMARY
                                                                            }
                                                                        }}
                                                                        onClick={() => setAssignDialog({ 
                                                                            open: true, 
                                                                            project: project, 
                                                                            assignedEngineers: [] 
                                                                        })}
                                                                    >
                                                                        + Assign
                                                                    </Button>
                                                                )}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                )}

                                                 {/* Status Column */}
                                                 <TableCell sx={{
                                                     width: '140px',
                                                     minWidth: '140px',
                                                     maxWidth: '140px',
                                                     padding: "8px 6px"
                                                 }}>
                                                     <Chip
                                                         label={currentStatus}
                                                         size="small"
                                                         sx={{
                                                             ...(statusStyles[currentStatus.toUpperCase()] || {}),
                                                             height: 'auto',
                                                             py: 0.5,
                                                             ...TYPOGRAPHY.BODY_SMALL,
                                                             fontSize: "0.65rem",
                                                             textTransform: 'capitalize',
                                                             whiteSpace: 'normal',
                                                             textAlign: 'center',
                                                             '& .MuiChip-label': {
                                                                 padding: '0 6px',
                                                                 display: 'block',
                                                                 whiteSpace: 'normal',
                                                             }
                                                         }}
                                                     />
                                                 </TableCell>

                                                {/* Mapping Column (View & Edit) */}
                                                <TableCell align="center" sx={{
                                                    width: '100px',
                                                    minWidth: '100px',
                                                    maxWidth: '100px',
                                                    padding: "8px 6px"
                                                }}>
                                                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                                        <CustomTooltip title="View Mapping">
                                                            <IconButton
                                                                onClick={() => {
                                                                    const targetId = project.associatedProject?._id || project.associatedProject || project._id;
                                                                    navigate(`/technical/view-mapping/${targetId}`);
                                                                }}
                                                                sx={{
                                                                    color: COLORS.SECONDARY,
                                                                    padding: "2px",
                                                                    minWidth: '24px',
                                                                    height: '24px'
                                                                }}
                                                            >
                                                                <Icon name="eye" size={20} />
                                                            </IconButton>
                                                        </CustomTooltip>

                                                        {!hasRole("ENGINEER") && !hasRole("CLIENT") && (
                                                            <CustomTooltip title={((project.source === "FIR" || source === "FIR") && (!project.assignedEngineers || project.assignedEngineers.length === 0)) ? "Please assign Engineer first" : "Edit Mapping"}>
                                                                <IconButton
                                                                    onClick={() => {
                                                                        const isUnassignedFir = (project.source === "FIR" || source === "FIR") && (!project.assignedEngineers || project.assignedEngineers.length === 0);
                                                                        if (isUnassignedFir) {
                                                                            setAlertModal({ open: true, message: "Please assign engineer to create a project from client request to edit mapping." });
                                                                        } else {
                                                                            const targetId = project.associatedProject?._id || project.associatedProject || project._id;
                                                                            navigate(`/technical/start-mapping/${targetId}`);
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        color: COLORS.PRIMARY,
                                                                        padding: "2px",
                                                                        minWidth: '24px',
                                                                        height: '24px'
                                                                    }}
                                                                >
                                                                    <Icon name="edit" size={20} />
                                                                </IconButton>
                                                            </CustomTooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>

                                                {/* Actions Column */}
                                                <TableCell align="center" sx={{
                                                    width: '120px',
                                                    minWidth: '120px',
                                                    maxWidth: '120px',
                                                    padding: "8px 6px"
                                                }}>
                                                    <Box sx={{ display: "flex", gap: 1, flexWrap: 'wrap', justifyContent: "center" }}>
                                                        <CustomTooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => navigate(`/technical/project-details/${project._id}`)}
                                                                sx={{
                                                                    padding: "2px",
                                                                    minWidth: '24px',
                                                                    height: '24px'
                                                                }}
                                                            >
                                                                <Icon name="Action1" color={COLORS.TEXT} />
                                                            </IconButton>
                                                        </CustomTooltip>

                                                        {isCompleted && (
                                                            <CustomTooltip title="Download Report">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDownloadReport(project._id || project.id, project)}
                                                                    disabled={downloadProgress[project._id || project.id] !== undefined}
                                                                    sx={{
                                                                        padding: "2px",
                                                                        minWidth: '24px',
                                                                        height: '24px',
                                                                        position: 'relative'
                                                                    }}
                                                                >
                                                                    {downloadProgress[project._id || project.id] !== undefined ? (
                                                                        <>
                                                                            <CircularProgress variant={downloadProgress[project._id || project.id] > 0 && downloadProgress[project._id || project.id] < 100 ? "determinate" : "indeterminate"} value={downloadProgress[project._id || project.id]} size={18} color="inherit" />
                                                                            {downloadProgress[project._id || project.id] > 0 && downloadProgress[project._id || project.id] < 100 && (
                                                                                <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                    <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: '0.45rem', fontWeight: 'bold' }}>
                                                                                        {`${downloadProgress[project._id || project.id]}%`}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <Icon name="download1" color={COLORS.SECONDARY} />
                                                                    )}
                                                                </IconButton>
                                                            </CustomTooltip>
                                                        )}


                                                        {(!hasRole("ENGINEER") && (!hasRole("CLIENT") || (!project.assignedEngineers || project.assignedEngineers.length === 0))) && (
                                                            <CustomTooltip title="Edit Project Details">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        if (project?.source === "FIR" || source === "FIR" || project?.propertyType) {
                                                                            navigate(`/admin/fir-requests/edit/${project._id}`, { state: { request: project } });
                                                                        } else {
                                                                            navigate(`/technical/add-project`, { state: { project: project } });
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        padding: "2px",
                                                                        minWidth: '24px',
                                                                        height: '24px'
                                                                    }}
                                                                >
                                                                    <Icon name="edit1" color={COLORS.SECONDARY} />
                                                                </IconButton>
                                                            </CustomTooltip>
                                                        )}

                                                        {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
                                                            <CustomTooltip title="Delete Project">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => triggerDelete(project._id, source === "FIR" || project.source === "FIR", !!project.originalFirId || !!project.firRequestId)}
                                                                    sx={{
                                                                        padding: "2px",
                                                                        minWidth: '24px',
                                                                        height: '24px'
                                                                    }}
                                                                >
                                                                    <Icon name="Action4" color={COLORS.ERROR} />
                                                                </IconButton>
                                                            </CustomTooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    {/* Loader - outside table */}
                    {(!engineersLoaded || (loading && projects.length === 0)) && (
                        <Box sx={{ width: '100%', textAlign: 'center', py: 5 }}>
                            <CircularProgress sx={{ color: COLORS.PRIMARY }} />
                        </Box>
                    )}

                    {/* Empty State Message - outside table to avoid disturbing column layout */}
                    {engineersLoaded && !(loading && projects.length === 0) && processedProjects.length === 0 && (
                        <Box sx={{ width: '100%', textAlign: 'center', py: 5 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.MUTED_TEXT }}>
                                {!isFilterApplied()
                                    ? "No projects found."
                                    : "No projects found with selected filters"}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Calendar Menu */}
                <Menu
                    anchorEl={calendarAnchorEl}
                    open={Boolean(calendarAnchorEl)}
                    onClose={() => setCalendarAnchorEl(null)}
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
                            px: 0.5,
                        },
                    }}
                >
                    {[
                        { label: "All Projects", value: "ALL", icon: "calendarAll" },
                        { label: "Today", value: "TODAY", icon: "calendarToday" },
                        { label: "Yesterday", value: "YESTERDAY", icon: "calendarYesterday" },
                        { label: "Tomorrow", value: "TOMORROW", icon: "calendarTomorrow" },
                        { label: "Custom Date", value: "CUSTOM", icon: "calendarCustom" },
                    ].map((item) => {
                        const isActive = dateFilter === item.value;

                        return (
                            <MenuItem
                                key={item.value}
                                onClick={() => handleCalendarFilterSelect(item.value)}
                                sx={{
                                    gap: 1.5,
                                    borderRadius: 1,
                                    bgcolor: "transparent !important",
                                    "&:hover": { bgcolor: "transparent" },
                                    color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                                }}
                            >
                                <Icon
                                    name={item.icon}
                                    size={18}
                                    color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                                />
                                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                                    {item.label}
                                </Typography>
                            </MenuItem>
                        );
                    })}
                </Menu>

                {/* Sort Menu */}
                <Menu
                    anchorEl={sortAnchorEl}
                    open={Boolean(sortAnchorEl)}
                    onClose={() => setSortAnchorEl(null)}
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
                            px: 0.5,
                        },
                    }}
                >
                    {[
                        { label: "Latest First", value: "latest", icon: "foldUp" },
                        { label: "Oldest First", value: "oldest", icon: "foldDown" },
                        { label: "A to Z", value: "AZ", icon: "foldUp" },
                        { label: "Z to A", value: "ZA", icon: "foldDown" },
                    ].map((item) => {
                        const isActive = selectedSort === item.value && sortConfig.column === null;

                        return (
                            <MenuItem
                                key={item.value}
                                onClick={() => {
                                    setSelectedSort(item.value);
                                    setSortConfig({ column: null, direction: null });
                                    setSortAnchorEl(null);
                                }}
                                sx={{
                                    gap: 1.5,
                                    borderRadius: 1,
                                    bgcolor: "transparent !important",
                                    "&:hover": { bgcolor: "transparent" },
                                    color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                                }}
                            >
                                <Icon
                                    name={item.icon}
                                    size={18}
                                    color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                                />
                                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                                    {item.label}
                                </Typography>
                            </MenuItem>
                        );
                    })}
                </Menu>

                {/* Custom Date Picker Dialog */}
                <Dialog
                    open={showDatePicker}
                    onClose={() => setShowDatePicker(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Icon name="calendarCustom" color={COLORS.PRIMARY} />
                            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                                Select Date
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                            <DatePicker
                                label="Select Date"
                                format="dd/MM/yyyy"
                                value={tempDate}
                                onChange={(newValue) => {
                                    setTempDate(newValue);
                                }}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button
                            onClick={() => setShowDatePicker(false)}
                            sx={{
                                color: COLORS.TEXT,
                                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                if (tempDate) {
                                    setSelectedDate(tempDate);
                                    setDateFilter("CUSTOM");
                                    setShowDatePicker(false);
                                }
                            }}
                            disabled={!tempDate}
                            sx={{
                                bgcolor: COLORS.SECONDARY,
                                color: "#fff",
                                "&:hover": { bgcolor: COLORS.SECONDARY },
                                "&:disabled": { bgcolor: COLORS.LIGHT_SECONDARY }
                            }}
                        >
                            Apply Date
                        </Button>
                    </DialogActions>
                </Dialog>



                <ConfirmDialog
                    open={deleteConfirm.open}
                    title="Confirm Delete"
                    message="Are you sure you want to delete this project? This action cannot be undone."
                    onConfirm={executeDelete}
                    onCancel={() => setDeleteConfirm({ open: false, id: null })}
                />

                {/* Assign Engineer Dialog */}
                <Dialog 
                    open={assignDialog.open} 
                    onClose={() => setAssignDialog({ open: false, project: null, assignedEngineers: [] })}
                    PaperProps={{
                        sx: {
                            borderRadius: "16px",
                            padding: "10px",
                            minWidth: { xs: '300px', sm: '400px' }
                        }
                    }}
                >
                    <DialogTitle sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
                        Assign Engineers
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ mb: 1, fontFamily: FONT_FAMILY, fontWeight: 500, color: COLORS.PRIMARY }}>
                                Select Engineers for {assignDialog.project?.projectName}
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    multiple
                                    displayEmpty
                                    value={assignDialog.assignedEngineers}
                                    onChange={(e) => setAssignDialog(prev => ({ ...prev, assignedEngineers: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value }))}
                                    input={<OutlinedInput />}
                                    renderValue={(selected) => {
                                        if (selected.length === 0) {
                                            return <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: '14px' }}>Select Engineers</Typography>;
                                        }
                                        return (
                                            <Box sx={{ display: 'flex', flexWrap: 'nowrap', overflowX: 'auto', gap: 0.5, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                                {selected.map((value) => {
                                                    const eng = engineers.find(e => e._id === value);
                                                    return <Chip key={value} label={eng ? eng.name : value} size="small" sx={{ height: 24, fontSize: '12px', flexShrink: 0 }} />;
                                                })}
                                            </Box>
                                        );
                                    }}
                                    MenuProps={commonMenuProps}
                                    sx={{
                                        fontFamily: FONT_FAMILY,
                                        fontSize: '14px',
                                        borderRadius: '8px',
                                    }}
                                >
                                    {engineers.map((eng) => (
                                        <MenuItem key={eng._id} value={eng._id} sx={{ fontFamily: FONT_FAMILY, fontSize: '14px' }}>
                                            <Checkbox checked={assignDialog.assignedEngineers.indexOf(eng._id) > -1} />
                                            <ListItemText primary={eng.name} primaryTypographyProps={{ fontFamily: FONT_FAMILY, fontSize: '14px' }} />
                                        </MenuItem>
                                    ))}
                                    {engineers.length === 0 && (
                                        <MenuItem disabled>No approved engineers found</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ padding: "16px 24px" }}>
                        <Button 
                            onClick={() => setAssignDialog({ open: false, project: null, assignedEngineers: [] })}
                            sx={{ color: COLORS.MUTED_TEXT, textTransform: "none", fontFamily: FONT_FAMILY }}
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleAssignSubmit}
                            variant="contained"
                            disabled={assigningLoading}
                            sx={{ 
                                backgroundColor: COLORS.PRIMARY, 
                                "&:hover": { backgroundColor: COLORS.SECONDARY },
                                textTransform: "none",
                                fontFamily: FONT_FAMILY,
                                borderRadius: "8px",
                                px: 3
                            }}
                        >
                            {assigningLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : "Assign"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Alert Modal */}
                <Dialog 
                    open={alertModal.open} 
                    onClose={() => setAlertModal({ open: false, message: "" })}
                    PaperProps={{
                        sx: {
                            borderRadius: "16px",
                            padding: "10px",
                            minWidth: { xs: '300px', sm: '400px' }
                        }
                    }}
                >
                    <DialogTitle sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
                        Action Required
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 1 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, color: COLORS.TEXT }}>
                                {alertModal.message}
                            </Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ padding: "16px 24px" }}>
                        <Button 
                            onClick={() => setAlertModal({ open: false, message: "" })}
                            variant="contained"
                            sx={{ 
                                backgroundColor: COLORS.PRIMARY, 
                                "&:hover": { backgroundColor: COLORS.SECONDARY },
                                textTransform: "none",
                                fontFamily: FONT_FAMILY,
                                borderRadius: "8px",
                                px: 3
                            }}
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
            <FIRRequestDetailsModal
                open={firModalState.open}
                onClose={() => setFirModalState({ open: false, request: null, editMode: false })}
                request={firModalState.request}
                initialEditMode={firModalState.editMode}
                onApproveSuccess={() => {
                    setFirModalState({ open: false, request: null, editMode: false });fetchProjects();
                }}
            />


        </LocalizationProvider>
    );
};

export default ProjectList;